import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, lastValueFrom, timeout, BehaviorSubject, throwError } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';

import { AppConfigService } from '~/core/services/appconfig.service';
import { AppConfig } from '~/core/models/appconfig';

const TIMEOUT: number = 30000;

@Injectable({
    providedIn: 'root'
})
export class HttpService {
    private config: AppConfig;
    private apiHeader: HttpHeaders = new HttpHeaders({ "accept": "text/plain", "Content-Type": "application/json" });
    private tokenSubject = new BehaviorSubject<string | null>(null);
    public token$ = this.tokenSubject.asObservable();

    constructor(
        private http: HttpClient,
        private appconfigService: AppConfigService
    ) {
        this.config = this.appconfigService.getConfig();
        // Load existing token from localStorage
        const savedToken = localStorage.getItem('wells_api_token');
        if (savedToken) {
            this.tokenSubject.next(savedToken);
        }
    }

    public Get<T>(endpoint: string, headers: HttpHeaders = this.apiHeader, callTimeout: number = TIMEOUT): Observable<T> {
        return this.http.get<T>(`${this.config.apiUrl}/${endpoint}`, { headers: headers }).pipe(timeout(callTimeout));
    }

    public Post<T>(endpoint: string, body?: any, headers: HttpHeaders = this.apiHeader, callTimeout: number = TIMEOUT): Observable<T> {
        return this.http.post<T>(`${this.config.apiUrl}/${endpoint}`, body, { headers: headers }).pipe(timeout(callTimeout));
    }

    public async GetAsset(file: string) {
        let results: any = null;

        try {
            let stream$ = this.http.get(`assets/${file}`);

            results = await lastValueFrom(stream$);
        } catch (error: any) {
            throw error;
        }

        return results;
    }

    public GetExternal<T>(url: string, callTimeout: number = TIMEOUT): Observable<T> {
        return this.http.get<T>(url).pipe(timeout(callTimeout));
    }

    //  Wells API Authentication Methods
    public issueWellsToken(email: string = 'test@example.com'): Observable<any> {
        // Refresh config to ensure we have the latest values
        this.config = this.appconfigService.getConfig();
        
        
        if (!this.config.wellsApiKey) {
            throw new Error('Wells API key is not configured');
        }
        
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-API-Key': this.config.wellsApiKey
        });

        const body = { email: email || '' };

        return this.http.post(`${this.config.apiUrl}/token/test`, body, { headers })
            .pipe(
                timeout(TIMEOUT),
                map((response: any) => {
                    if (response.access_token || response.token) {
                        const token = response.access_token || response.token;
                        this.setWellsToken(token);
                        return response;
                    }
                    throw new Error('No token received in response');
                }),
                catchError((error: any) => {
                    throw error;
                })
            );
    }

    public setWellsToken(token: string): void {
        this.tokenSubject.next(token);
        localStorage.setItem('wells_api_token', token);
    }

    public getWellsToken(): string | null {
        return this.tokenSubject.value || localStorage.getItem('wells_api_token');
    }

    public clearWellsToken(): void {
        this.tokenSubject.next(null);
        localStorage.removeItem('wells_api_token');
    }

    public isTokenValid(): boolean {
        const token = this.getWellsToken();
        if (!token) {
            return false;
        }

        try {
            // Decode JWT token to check expiration
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            const expirationTime = payload.exp;
            
            const isValid = expirationTime > currentTime;
            
            return isValid;
        } catch (error) {
            return false;
        }
    }

     private getWellsApiHeaders(): HttpHeaders {
        // Refresh config to ensure we have the latest values
        this.config = this.appconfigService.getConfig();
        
        const token = this.getWellsToken();
        
        
        if (!this.config.wellsApiKey) {
          
            throw new Error('Wells API key is not configured');
        }
        
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-API-Key': this.config.wellsApiKey,
            'Accept': 'application/json'
        });

        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        } else {
        }

        return headers;
    }

    public getWellsList(includeAll: boolean = true): Observable<any> {
        
        const token = this.getWellsToken();
        
        if (!token) {
            throw new Error('No authentication token available. Please authenticate first.');
        }
        
        // Check if token is valid before using it
        if (!this.isTokenValid()) {
            throw new Error('Authentication token is invalid or expired. Please re-authenticate.');
        }
        
        const headers = this.getWellsApiHeaders();
        
        // Create params object to include the option to fetch all wells
        const params: { [key: string]: string } = includeAll ? { all: 'true' } : {};
        
        return this.http.get(`${this.config.apiUrl}/api/wells_list`, { 
            headers,
            params
        }).pipe(
            timeout(TIMEOUT),
            map((response: any) => {
                // Ensure we're handling all possible response formats
                if (Array.isArray(response)) {
                    return response;
                } else if (response && response.wells && Array.isArray(response.wells)) {
                    return response.wells;
                } else if (response && typeof response === 'object') {
                    // If response is an object but doesn't have a wells property,
                    // try to convert it to an array of wells
                    return Object.keys(response).map(key => {
                        if (typeof response[key] === 'object' && response[key].name) {
                            return response[key];
                        } else {
                            return { name: key, ...response[key] };
                        }
                    });
                }
                // Return empty array as fallback
                return [];
            }),
            catchError((error: any) => {
                if (error.status === 401) {
                    this.clearWellsToken();
                }
                throw error;
            })
        );
    }

    public getSchemaList(wellName: string): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const params = { well_name: wellName };
        
        return this.http.get(`${this.config.apiUrl}/api/schema/list`, { 
            headers, 
            params 
        }).pipe(timeout(TIMEOUT));
    }

    public getWellSchema(wellName: string, schemaType: string): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const params = { 
            well_name: wellName, 
            schema_type: schemaType 
        };
        
        return this.http.get(`${this.config.apiUrl}/api/schema`, { 
            headers, 
            params 
        }).pipe(timeout(TIMEOUT));
    }

    public saveEngineerSuggestions(
        wellName: string, 
        schemaType: string, 
        userId: string, 
        suggestions: any[]
    ): Observable<any> {
        const headers = this.getWellsApiHeaders();
        
        // Extract just the record data from each suggestion object
        const formattedSuggestions = suggestions.map(item => {
            // If the item has a 'record' property, use that directly
            return item.record || item;
        });
        
        // For direct array format (without any wrapper)
        const directArrayPayload = formattedSuggestions;
        
        // First try with URL parameters and the direct array payload (no wrapper)
        const url = `${this.config.apiUrl}/api/suggestions?well_name=${encodeURIComponent(wellName)}&schema_type=${encodeURIComponent(schemaType)}&user_id=${encodeURIComponent(userId)}`;
        
        return this.http.post(url, directArrayPayload, { headers })
            .pipe(
                timeout(TIMEOUT),
                catchError(error => {
                    // Add detailed logging for troubleshooting
                    if (error.error) {
                        // Error response body received
                    }
                    
                    // Try with the wrapped format if direct array fails
                    const wrappedPayload = { suggestions: formattedSuggestions };
                    
                    return this.http.post(url, wrappedPayload, { headers }).pipe(
                        timeout(TIMEOUT),
                        catchError(secondError => {
                            
                            // Final attempt: try with payload in URL and direct array in body
                            return this.http.post(`${this.config.apiUrl}/api/suggestions`, directArrayPayload, { 
                                headers,
                                params: {
                                    well_name: wellName,
                                    schema_type: schemaType,
                                    user_id: userId
                                }
                            }).pipe(
                                timeout(TIMEOUT),
                                catchError(thirdError => {
                                    throw thirdError;
                                })
                            );
                        })
                    );
                })
            );
    }

    // Combined method to authenticate and then get wells list
    public authenticateAndGetWells(email: string = 'test@example.com'): Observable<any> {
        
        this.clearWellsToken();
        
        return this.issueWellsToken(email).pipe(
            tap((tokenResponse: any) => {
                const token = this.getWellsToken();
                
                // Ensure token is properly set before continuing
                if (!token) {
                    throw new Error('Token was not properly stored after authentication');
                }
            }),
            switchMap((tokenResponse: any) => {
                
                // Double-check we have the token before making the request
                const currentToken = this.getWellsToken();
                if (!currentToken) {
                    throw new Error('Token lost between authentication and wells request');
                }
                
                 return this.getWellsList();
            }),
            catchError((error: any) => {
                 this.clearWellsToken(); 
                throw error;
            })
        );
    }

    
    public testDirectWellsAPI(): Observable<any> {
    
        this.config = this.appconfigService.getConfig();
        
        // Get token
        const token = this.getWellsToken();
        
        if (!token) {
            throw new Error('No token available for direct test');
        }
        
        if (!this.config.wellsApiKey) {
            throw new Error('No API key available for direct test');
        }
        
        // Manually construct headers
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('X-API-Key', this.config.wellsApiKey)
            .set('Authorization', `Bearer ${token}`);
        
        const url = `${this.config.apiUrl}/api/wells_list`;
        
        return this.http.get(url, { headers }).pipe(
            timeout(TIMEOUT),
            tap((response: any) => {
               
            }),
            catchError((error: any) => {
                
                throw error;
            })
        );
    }

    public getSasUrl(blobPath: string): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const trimmedPath = blobPath.trim();
        const url = `${this.config.apiUrl}/api/sas-url`;
        return this.http.get(url + `?file_path=${trimmedPath}`, { headers });
    }

    /**
     * Fetch the list of user IDs from the API
     * GET /api/user_ids
     * response format : { user_ids: { user_id: string }[] }
     */
    public getUserIds(): Observable<{ user_ids: { user_id: string }[] }> {
        const headers = this.getWellsApiHeaders(); // Ensure auth headers are included
        return this.http.get<{ user_ids: { user_id: string }[] }>(`${this.config.apiUrl}/api/user_ids`, { headers }).pipe(timeout(TIMEOUT));
    }

    /**
     * Save a single record to the suggestions API
     * This method is specifically designed to update only one record at a time
     */
    public saveSingleRecord(
        wellName: string, 
        schemaType: string, 
        userId: string, 
        record: any
    ): Observable<any> {
        const headers = this.getWellsApiHeaders();
        
        // Create URL with parameters
        const url = `${this.config.apiUrl}/api/suggestions?well_name=${encodeURIComponent(wellName)}&schema_type=${encodeURIComponent(schemaType)}&user_id=${encodeURIComponent(userId)}`;
        
        // First approach: try sending just the record as a single object
        return this.http.post(url, record, { headers })
            .pipe(
                timeout(TIMEOUT),
                catchError(error => {
                    // Second approach: try wrapping in an array
                    return this.http.post(url, [record], { headers }).pipe(
                        timeout(TIMEOUT),
                        catchError(secondError => {
                            // Third approach: try with suggestions wrapper
                            const wrappedPayload = { suggestions: [record] };
                            
                            return this.http.post(url, wrappedPayload, { headers }).pipe(
                                timeout(TIMEOUT),
                                catchError(thirdError => {
                                    throw thirdError;
                                })
                            );
                        })
                    );
                })
            );
    }

    /**
     * Delete a single record using the API
     * This method is specifically designed to delete one record at a time
     */
    public deleteRecord(
        wellName: string, 
        schemaType: string, 
        userId: string, 
        recordId: string
    ): Observable<any> {
        const headers = this.getWellsApiHeaders();
        
        // Create URL with parameters for the DELETE request
        const url = `${this.config.apiUrl}/api/suggestions?well_name=${encodeURIComponent(wellName)}&schema_type=${encodeURIComponent(schemaType)}&user_id=${encodeURIComponent(userId)}&record_id=${encodeURIComponent(recordId)}`;
        
        // Make DELETE request to remove the record
        return this.http.delete(url, { headers })
            .pipe(
                timeout(TIMEOUT),
                catchError(error => {
                    // Second approach: try with a different format if needed
                    if (error.status === 404 || error.status === 400) {
                        // Some APIs implement deletion via POST with an action parameter
                        return this.http.post(`${this.config.apiUrl}/api/suggestions`, 
                            { action: 'delete', record_id: recordId },
                            { 
                                headers,
                                params: {
                                    well_name: wellName,
                                    schema_type: schemaType,
                                    user_id: userId
                                }
                            }).pipe(
                                timeout(TIMEOUT),
                                catchError(secondError => {
                                    throw secondError;
                                })
                            );
                    }
                    
                    throw error;
                })
            );
    }

    /**
     * Retrieves well files by type (DDR, DTR, WSS) with SAS URLs
     * @param wellName The name of the well
     * @param fileType The type of file (ddr, dtr, wss)
     * @returns Observable with list of matching file names and their SAS URLs
     */
    public getWellFiles(wellName: string, fileType: string): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const params = { 
            well_name: wellName, 
            file_type: fileType 
        };
        
        return this.http.get(`${this.config.apiUrl}/api/well_files`, { 
            headers, 
            params 
        }).pipe(
            timeout(TIMEOUT),
            tap(response => {
                // Process response
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    /**
     * Get most recent engineer suggestions logs for a particular well and schema
     * 
     * @param wellName The name of the well
     * @param schemaType The schema type
     * @returns Observable with the latest log file content if available
     */
    public getEngineerSuggestionsLogs(wellName: string, schemaType: string): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const params = { 
            well_name: wellName, 
            schema_type: schemaType 
        };
        
        return this.http.get(`${this.config.apiUrl}/api/suggestions/logs`, { 
            headers, 
            params 
        }).pipe(
            timeout(TIMEOUT),
            map((response: any) => {
                // Determine if the response is an array or an object with logs property
                let logsArray: any[] = [];
                
                if (Array.isArray(response)) {
                    logsArray = [...response];
                } else if (response && response.logs && Array.isArray(response.logs)) {
                    logsArray = [...response.logs];
                } else if (response && typeof response === 'object') {
                    // Try to extract any array-like data from the response
                    const possibleArrays = Object.values(response).filter(value => Array.isArray(value));
                    if (possibleArrays.length > 0) {
                        // Use the first array found in the response
                        logsArray = [...possibleArrays[0]];
                    } else {
                        return { logs: [] };
                    }
                } else {
                    // Return standardized format with empty logs
                    return { logs: [] };
                }
                
                // Preserve exact fields from the API response without transformations
                // Only wrap in a logs property if not already present
                const processedResponse = { logs: logsArray };
                return processedResponse;
            }),
            catchError(error => {
                return throwError(() => error);
            })
        );
    }

    /**
     * Get change logs for a particular well and schema
     */
    public getChangeLogs(wellName: string, schemaType: string): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const params = { 
            well_name: wellName, 
            schema_type: schemaType 
        };
        
        return this.http.get(`${this.config.apiUrl}/api/change-logs`, { 
            headers, 
            params 
        }).pipe(
            timeout(TIMEOUT),
            map((response: any) => {
                // Process the response to ensure it's in the expected format
                if (Array.isArray(response)) {
                    return response;
                } else if (response && response.logs && Array.isArray(response.logs)) {
                    return response.logs;
                } else if (response && typeof response === 'object') {
                    // Try to find any property that contains an array of logs
                    const possibleArrays = Object.values(response).filter(val => Array.isArray(val));
                    if (possibleArrays.length > 0) {
                        return possibleArrays[0];
                    }
                }
                
                // Return empty array as fallback
                return [];
            }),
            catchError(error => {
                return throwError(() => error);
            })
        );
    }

    /**
     * Get all engineer suggestions logs for a particular well
     * 
     * @param wellName The name of the well
     * @returns Observable with all available log entries for the well
     */
    public getRecentEngineerSuggestionsLogs(wellName: string): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const params = { 
            well_name: wellName,
            limit: '1000' // Request a large number to effectively get all logs
        };
        
        return this.http.get(`${this.config.apiUrl}/api/suggestions/logs`, { 
            headers, 
            params 
        }).pipe(
            timeout(TIMEOUT),
            map((response: any) => {
                // Determine if the response is an array or an object with logs property
                let logsArray: any[] = [];
                
                if (Array.isArray(response)) {
                    logsArray = [...response];
                } else if (response && response.logs && Array.isArray(response.logs)) {
                    logsArray = [...response.logs];
                } else if (response && typeof response === 'object') {
                    // Try to extract any array-like data from the response
                    const possibleArrays = Object.values(response).filter(value => Array.isArray(value));
                    if (possibleArrays.length > 0) {
                        // Use the first array found in the response
                        logsArray = [...possibleArrays[0]];
                    } else {
                        return { logs: [] };
                    }
                } else {
                    // Return standardized format with empty logs
                    return { logs: [] };
                }
                
                // Preserve exact fields from the API response without transformations
                // Only wrap in a logs property if not already present
                const processedResponse = { logs: logsArray };
                return processedResponse;
            }),
            catchError(error => {
                return throwError(() => error);
            })
        );
    }
}
